/*
 * A component which renders the Khan Academy logo which resides in the top
 * header bar.
 */
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    markMinorConversions,
    type MinorConversionId,
} from "@repo/analytics-learning-time";

import Logo from "./logo.tsx";

type DefaultProps = {
    // Modifies logo color depending on the header background color
    backgroundColor: "white" | "darkBlue";
};

type Props = DefaultProps & {
    // Extra attributes to be applied to the logo link
    linkAttributes?: Record<string, any>;
    // Extra styles to be applied to the logo.
    logoStyles?: StyleType;
    // A optional big-bingo conversion to mark when the component is clicked.
    conversion?: MinorConversionId;
};

class HeaderLogo extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        backgroundColor: "white",
    };

    render(): React.ReactNode {
        const {conversion} = this.props;
        const isTransparent = this.props.backgroundColor !== "white";

        return (
            <Clickable
                testId="header-logo"
                href="/"
                aria-label="Khan Academy"
                style={[
                    this.props.logoStyles,
                    styles.link,
                    isTransparent && styles.transparent,
                ]}
                safeWithNav={async () => {
                    if (conversion) {
                        await markMinorConversions([
                            {
                                id: conversion,
                            },
                        ]);
                    }
                }}
            >
                {() => <Logo />}
            </Clickable>
        );
    }
}

const styles = StyleSheet.create({
    link: {
        // Do not use Color.darkBlue here because it was updated
        // on 8/8/22 to be darker. The branding color is still
        // the old blue, which is being used here.
        color: "#0a2a66",

        ":hover": {
            color: color.blue,
        },

        ":focus": {
            outline: `4px solid ${color.blue}`,
        },
    },

    transparent: {
        color: color.white,

        "@media print": {
            // Printable reports should always show logo with dark blue text
            color: "#0a2a66",
        },
    },
});

export default HeaderLogo;
